import { useEffect, useState } from "react";
import { Input, ButtonSiguiente, TextareaComun, Select } from "../../ui";
import { safeFetch } from "@/api/safeFetch";
import { useNavigate, useParams } from "react-router-dom";
import { PopupComentarios } from "@/components/popup";

interface Direccion {
    calle?: string;
    localidad?: string;
    provincia?: string;
    codigo_postal?: string;
    pais?: string;
}

interface Empresa {
    nombre_empresa?: string;
    cif?: string;
    correo_perfil?: string;
}

interface Perfil {
    perfil_profesional_id?: number;
    descripcion?: string;
    foto_perfil?: string;
    direccion: Direccion;
    empresa?: Empresa;
}

interface Profesional {
    usuario_id?: number;
    nombre?: string;
    apellidos?: string;
    email?: string;
    dni?: string | null;
    nie?: string | null;
    profesional_id?: number | null;
    empresa?: Empresa | null;
    perfiles: Perfil[];

}

const EditarProfesionales: React.FC = () => {
    const [busqueda, setBusqueda] = useState("");
    const [profesional, setProfesional] = useState<Profesional | null>(null);
    const [perfilSeleccionado, setPerfilSeleccionado] = useState<Perfil | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [foto, setFoto] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [showComentarios, setShowComentarios] = useState(false);
    const navigate = useNavigate();
    const token = localStorage.getItem("token") ?? "";

    const { profesionalId, perfilProfesionalId } = useParams<{
        profesionalId?: string;
        perfilProfesionalId?: string;
    }>();


    useEffect(() => {
        return () => {
            if (preview && preview.startsWith("blob:")) {
                URL.revokeObjectURL(preview);
            }
        };
    }, [preview]);


    useEffect(() => {
        if (profesionalId) {
            setBusqueda(profesionalId);
            handleBuscar(perfilProfesionalId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profesionalId]);

    const normalizeBackendRows = (rows: any[]): Profesional[] => {
        // Agrupa por usuario_id
        const map = new Map<number, Profesional>();

        rows.forEach(row => {

            const id = row.usuario_id;
            const perfil: Perfil = {
                perfil_profesional_id: row.perfil_profesional_id,
                descripcion: row.descripcion ?? "",
                foto_perfil: row.foto_perfil ?? "",
                direccion: {
                    calle: row.calle ?? "",
                    localidad: row.localidad ?? "",
                    provincia: row.provincia ?? "",
                    codigo_postal: row.codigo_postal ?? "",
                    pais: row.pais ?? "",
                },
                empresa: {
                    nombre_empresa: row.nombre_empresa ?? "",
                    cif: row.cif ?? "",
                    correo_perfil: row.correo_perfil ?? ""
                }
            };

            if (map.has(id)) {
                // Agrega perfil al profesional existente
                map.get(id)?.perfiles.push(perfil);
            } else {
                // Crea nuevo profesional
                map.set(id, {
                    usuario_id: id,
                    nombre: row.nombre ?? "",
                    apellidos: row.apellidos ?? "",
                    email: row.email ?? "",
                    dni: row.dni ?? null,
                    nie: row.nie ?? null,
                    profesional_id: row.profesional_id ?? null,
                    empresa: {
                        nombre_empresa: row.nombre_empresa ?? "",
                        cif: row.cif ?? "",
                        correo_perfil: row.correo_perfil ?? "",
                    },
                    perfiles: [perfil]
                });
            }
        });

        return Array.from(map.values());
    };


    const handleBuscar = async (queryParam?: string) => {
        const controller = new AbortController();
        const signal = controller.signal;

        const query = (queryParam ?? busqueda)?.trim();
        if (!query) {
            setError("Introduce DNI / NIE / CIF o ID");
            return;
        }

        setError("");
        setSuccess("");
        setProfesional(null);
        setPerfilSeleccionado(null);
        setPreview(null);
        setFoto(null);

        try {
            const res = await safeFetch(
                `/api/profesionales/buscar?q=${encodeURIComponent(query)}`,
                { headers: { Authorization: `Bearer ${token}` }, signal }
            );

            if (!res.ok) {
                setError(res.status === 401 ? "No autorizado" : "Error al buscar profesional");
                return;
            }

            const data = await res.json();
            const resultados = Array.isArray(data)
                ? normalizeBackendRows(data)
                : normalizeBackendRows([data]);

            if (!resultados.length) {
                setError("Profesional no encontrado");
                return;
            }

            const primerProfesional = resultados[0];
            setProfesional(primerProfesional);
            setPerfilSeleccionado(primerProfesional.perfiles[0] ?? null);
            setPreview(null);

        } catch (err: any) {
            if (err.name !== "AbortError") {
                setError(err?.message ?? "Error de conexión");
            }
        }

        return () => controller.abort();
    };


    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;

        if (preview && preview.startsWith("blob:")) {
            URL.revokeObjectURL(preview);
        }

        setFoto(file);
        if (file) setPreview(URL.createObjectURL(file));
    };


    const updatePerfilSeleccionado = (updatedPerfil: Perfil) => {
        setPerfilSeleccionado(updatedPerfil);

        setProfesional(prev =>
            prev
                ? {
                    ...prev,
                    perfiles: prev.perfiles.map(p =>
                        p.perfil_profesional_id === updatedPerfil.perfil_profesional_id
                            ? updatedPerfil
                            : p
                    ),
                }
                : prev
        );
    };

    const handleActualizar = async () => {
        if (!perfilSeleccionado?.perfil_profesional_id || !profesional) {
            setError("Perfil profesional no válido");
            return;
        }

        setLoading(true);
        setError("");
        setSuccess("");

        try {
            const formData = new FormData();
            formData.append("usuario_id", String(profesional.usuario_id ?? ""));
            formData.append("nombre", profesional.nombre ?? "");
            formData.append("apellidos", profesional.apellidos ?? "");
            formData.append("email", profesional.email ?? "");
            formData.append("dni", profesional.dni ?? "");
            formData.append("nie", profesional.nie ?? "");
            formData.append("profesional_id", String(profesional.profesional_id ?? ""));
            formData.append("nombre_empresa", perfilSeleccionado.empresa?.nombre_empresa ?? "");
            formData.append("cif", perfilSeleccionado.empresa?.cif ?? "");
            formData.append("correo_perfil", perfilSeleccionado.empresa?.correo_perfil ?? "");
            formData.append("descripcion", perfilSeleccionado.descripcion ?? "");
            formData.append("direccion", JSON.stringify(perfilSeleccionado.direccion));
            if (foto) formData.append("foto_perfil", foto);

            const res = await safeFetch(
                `/api/profesionales/editar/${perfilSeleccionado.perfil_profesional_id}`,
                {
                    method: "PUT",
                    body: formData,
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            const data = await res.json().catch(() => null);
            if (!res.ok) throw new Error(data?.message || "Error al actualizar");

            const updatedPerfil: Perfil = {
                ...perfilSeleccionado,
                foto_perfil:
                    typeof data?.foto_perfil === "string" && data.foto_perfil.trim() !== ""
                        ? data.foto_perfil
                        : perfilSeleccionado.foto_perfil,
            };

            updatePerfilSeleccionado(updatedPerfil);

            // solo limpiar preview si se subió imagen nueva
            if (foto && preview) {
                URL.revokeObjectURL(preview);
                setPreview(null);
                setFoto(null);
            }


            setSuccess("Perfil actualizado correctamente 🎉");

        } catch (err: any) {
            setError(err?.message ?? "Error de actualización");
        } finally {
            setLoading(false);
        }
    };


    const imgSrc = preview
        ? preview
        : perfilSeleccionado?.foto_perfil
            ? `${import.meta.env.VITE_BACKEND_URL}${perfilSeleccionado.foto_perfil}`
            : "/img/logo/LogoLinkServs.png";

    useEffect(() => {
        return () => {
            // limpiar preview blob
            if (preview && preview.startsWith("blob:")) {
                URL.revokeObjectURL(preview);
            }

            // limpiar input file
            const inputs = document.querySelectorAll<HTMLInputElement>('input[type="file"]');
            inputs.forEach(input => (input.value = ""));

            // limpiar estados
            setBusqueda("");
            setProfesional(null);
            setPerfilSeleccionado(null);
            setPreview(null);
            setFoto(null);
            setError("");
            setSuccess("");
            setLoading(false);
            setShowComentarios(false);
        };
    }, []);


    // JSX ↓↓↓ (no tocado salvo limpieza menor)

    return (
        <div className="w-full min-h-screen flex items-center justify-center relative overflow-auto bg-zinc-500 p-4">

            {/* 🔙 Botón volver arriba izquierda */}
            <div className="absolute top-4 left-4">
                <ButtonSiguiente
                    onClick={() => navigate("/backoffice")}
                    className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition shadow"
                >
                    ⬅ Volver
                </ButtonSiguiente>
            </div>

            <div className="w-full max-w-3xl mx-auto bg-zinc-400 p-6 rounded-xl shadow-md border-zinc-600 border-r-4 border-t-2 flex flex-col gap-6 shadow-blue-700">

                <h2 className="text-2xl text-center font-bold">
                    Editar Profesional
                </h2>

                {/* 🔍 Buscador */}
                <div className="w-full bg-blue-600 p-4 rounded-xl flex gap-4 items-center border-zinc-200 border-r-4 border-t-2 border shadow-lg shadow-blue-700 flex-wrap">

                    <Input
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        placeholder="Introduce DNI / NIE / CIF / perfilId"
                        className="flex-1 min-w-[220px] h-10"
                    />

                    <ButtonSiguiente
                        onClick={() => handleBuscar()}
                        className="flex-1 min-w-[150px] !h-10"
                    >
                        Buscar
                    </ButtonSiguiente>

                    {profesional && profesional.perfiles?.length > 0 && (
                        <Select
                            value={perfilSeleccionado?.perfil_profesional_id || ""}
                            onChange={(e) => {
                                const id = Number(e.target.value);

                                const perfil =
                                    profesional.perfiles.find(
                                        p => p.perfil_profesional_id === id
                                    ) ?? null;

                                setPerfilSeleccionado(perfil);
                            }}
                            className="flex-1 min-w-[180px] h-10 p-2 rounded border bg-white text-black"
                            placeholder="-- Selecciona perfil --"
                            options={profesional.perfiles.map(p => ({
                                value: p.perfil_profesional_id ?? 0,
                                label: `Perfil ${p.perfil_profesional_id}`
                            }))}
                        />
                    )}
                </div>


                {error && <p className="text-red-600">{error}</p>}
                {success && <p className="text-green-600">{success}</p>}

                {profesional ? (
                    <div className="flex flex-col md:flex-row gap-6 overflow-y-auto flex-1">



                        {/* Información personal y empresa */}
                        <div className="flex-1 flex flex-col gap-4 pr-2">
                            <div>
                                <label className="block font-semibold mb-1">Nombre</label>
                                <Input value={profesional.nombre ?? ""} onChange={(e) => setProfesional({ ...profesional, nombre: e.target.value })} />
                            </div>
                            <div>
                                <label className="block font-semibold mb-1">Apellidos</label>
                                <Input value={profesional.apellidos ?? ""} onChange={(e) => setProfesional({ ...profesional, apellidos: e.target.value })} />
                            </div>
                            <div>
                                <label className="block font-semibold mb-1">Email</label>
                                <Input value={profesional.email ?? ""} onChange={(e) => setProfesional({ ...profesional, email: e.target.value })} />
                            </div>

                            <label className="block font-semibold mb-1">Nombre Empresa</label>
                            <Input value={perfilSeleccionado?.empresa?.nombre_empresa ?? ""} onChange={(e) => { if (!perfilSeleccionado) return; updatePerfilSeleccionado({ ...perfilSeleccionado, empresa: { ...(perfilSeleccionado.empresa ?? {}), nombre_empresa: e.target.value } }) }} />

                            <div>
                                <label className="block font-semibold mb-1">CIF Empresa</label>
                                <Input value={perfilSeleccionado?.empresa?.cif ?? ""} onChange={(e) => { if (!perfilSeleccionado) return; updatePerfilSeleccionado({ ...perfilSeleccionado, empresa: { ...(perfilSeleccionado.empresa ?? {}), cif: e.target.value } }) }} />
                            </div>
                            <div>
                                <label className="block font-semibold mb-1">Email Empresa</label>
                                <Input value={perfilSeleccionado?.empresa?.correo_perfil ?? ""} onChange={(e) => { if (!perfilSeleccionado) return; updatePerfilSeleccionado({ ...perfilSeleccionado, empresa: { ...(perfilSeleccionado.empresa ?? {}), correo_perfil: e.target.value } }) }} />
                            </div>

                            <div>
                                <label className="block font-semibold mb-1">Descripción</label>
                                <TextareaComun className="hover:border-zinc-700 hover:shadow-md" value={perfilSeleccionado?.descripcion ?? ""} onChange={(e) => {
                                    if (perfilSeleccionado) setPerfilSeleccionado({ ...perfilSeleccionado, descripcion: e.target.value });
                                }} />
                            </div>
                        </div>

                        {/* Dirección y foto */}
                        <div className="flex-1 flex flex-col gap-4 pr-2">
                            <div>
                                <label className="block font-semibold mb-1">Calle</label>
                                <Input value={perfilSeleccionado?.direccion.calle ?? ""} onChange={(e) => {
                                    if (perfilSeleccionado) setPerfilSeleccionado({ ...perfilSeleccionado, direccion: { ...(perfilSeleccionado.direccion), calle: e.target.value } });
                                }} />
                            </div>
                            <div>
                                <label className="block font-semibold mb-1">Localidad</label>
                                <Input value={perfilSeleccionado?.direccion.localidad ?? ""} onChange={(e) => {
                                    if (perfilSeleccionado) setPerfilSeleccionado({ ...perfilSeleccionado, direccion: { ...(perfilSeleccionado.direccion), localidad: e.target.value } });
                                }} />
                            </div>
                            <div>
                                <label className="block font-semibold mb-1">Provincia</label>
                                <Input value={perfilSeleccionado?.direccion.provincia ?? ""} onChange={(e) => {
                                    if (perfilSeleccionado) setPerfilSeleccionado({ ...perfilSeleccionado, direccion: { ...(perfilSeleccionado.direccion), provincia: e.target.value } });
                                }} />
                            </div>
                            <div>
                                <label className="block font-semibold mb-1">Código Postal</label>
                                <Input value={perfilSeleccionado?.direccion.codigo_postal ?? ""} onChange={(e) => {
                                    if (perfilSeleccionado) setPerfilSeleccionado({ ...perfilSeleccionado, direccion: { ...(perfilSeleccionado.direccion), codigo_postal: e.target.value } });
                                }} />
                            </div>
                            <div>
                                <label className="block font-semibold mb-1">País</label>
                                <Input value={perfilSeleccionado?.direccion.pais ?? ""} onChange={(e) => {
                                    if (perfilSeleccionado) setPerfilSeleccionado({ ...perfilSeleccionado, direccion: { ...(perfilSeleccionado.direccion), pais: e.target.value } });
                                }} />
                            </div>

                            <label className="flex items-center justify-center py-2 bg-zinc-600 text-white cursor-pointer rounded-md shadow-md shadow-zinc-700 mt-8 border">
                                Seleccionar Imagen
                                <input type="file" className="hidden" onChange={handleImageChange} />
                            </label>

                            <div className="w-full flex flex-col items-center gap-2">
                                <img
                                    src={imgSrc}
                                    onError={(e) => {
                                        e.currentTarget.src = "/img/logo/ImagenLogo.png";
                                    }}
                                    className="w-32 h-32 object-cover rounded border bg-white"
                                    alt="Foto perfil"
                                />

                                {foto && <span className="text-sm truncate">{foto.name}</span>}


                            </div>
                            {profesional && perfilSeleccionado && (
                                <ButtonSiguiente
                                    type="button"
                                    onClick={() => setShowComentarios(true)}
                                    className="bg-green-600 text-white py-2 rounded hover:bg-green-700 mt-4"
                                >
                                    Ver Comentarios
                                </ButtonSiguiente>
                            )}

                            {showComentarios && perfilSeleccionado && perfilSeleccionado.perfil_profesional_id ? (
                                <PopupComentarios
                                    perfil_id={perfilSeleccionado.perfil_profesional_id}
                                    onClose={() => setShowComentarios(false)}
                                />
                            ) : null}

                        </div>
                    </div>
                ) : (
                    <p className="text-zinc-500">Introduce DNI/NIE/CIF o perfilId arriba y pulsa Buscar</p>
                )}

                {profesional && (
                    <ButtonSiguiente type="button" onClick={handleActualizar} disabled={loading} className="bg-blue-600 text-white py-2 rounded hover:bg-blue-700 mt-4">
                        {loading ? "Actualizando..." : "Actualizar Profesional"}
                    </ButtonSiguiente>
                )}
            </div>
        </div>
    );
};

export default EditarProfesionales;
